import { 
  AnalysisConfiguration, 
  ChangeDirection, 
  ChangeSignificance, 
  DetectedChange, 
  MovementDataPoint, 
  MovementMetric, 
  MovementMetricType, 
  MovementTrend 
} from '../interfaces/types';

// Default configuration for pattern analysis
export const defaultAnalysisConfig: AnalysisConfiguration = {
  detectionThresholds: {
    GAIT: {
      minor: 3,
      moderate: 7,
      significant: 12
    },
    MOBILITY: {
      minor: 4,
      moderate: 8,
      significant: 15
    },
    ACTIVITY_LEVEL: {
      minor: 5,
      moderate: 10,
      significant: 20
    },
    SLEEP_PATTERN: {
      minor: 5,
      moderate: 10,
      significant: 20
    },
    BATHROOM_USAGE: {
      minor: 5,
      moderate: 10,
      significant: 20
    },
    POSTURE: {
      minor: 3,
      moderate: 8,
      significant: 15
    },
    BALANCE: {
      minor: 4,
      moderate: 8,
      significant: 15
    }
  },
  minDataPointsRequired: 14, // At least 2 weeks of data
  analysisWindowDays: 60, // Look at last 60 days
  sensitivityLevel: 'MEDIUM',
  enabledMetrics: ['GAIT', 'MOBILITY', 'ACTIVITY_LEVEL', 'BALANCE', 'POSTURE']
};

// Health implications of different metrics
const healthImplications: Record<string, string[]> = {
  stepLength: [
    'May indicate changes in muscle strength',
    'Could be linked to joint stiffness',
    'Often associated with balance issues'
  ],
  stepSymmetry: [
    'Asymmetry can indicate joint pain',
    'May be early sign of musculoskeletal issues',
    'Often precedes compensatory movement patterns'
  ],
  walkingSpeed: [
    'Strong predictor of overall health status',
    'Linked to cardiovascular capacity',
    'Correlated with cognitive function in older adults'
  ],
  cadence: [
    'Changes often reflect efficiency of movement',
    'May indicate neurological changes',
    'Can affect energy expenditure during activity'
  ],
  sitToStandTime: [
    'Strong indicator of lower body strength',
    'Linked to risk of falls',
    'Reflects functional independence'
  ],
  balanceScore: [
    'Direct relationship with fall risk',
    'May indicate vestibular system changes',
    'Can reflect neurological function'
  ],
  dailyStepCount: [
    'Indicator of overall activity level',
    'Linked to cardiovascular health',
    'May reflect changing mobility or motivation'
  ],
  variabilityIndex: [
    'Higher variability linked to fall risk',
    'May indicate neurological changes',
    'Can reflect adaptation to pain or discomfort'
  ],
  turnAngle: [
    'Restricted turning may indicate vestibular issues',
    'Can be related to balance confidence',
    'Often changes before straight-line walking issues'
  ],
  posturalSway: [
    'Increased sway linked to balance impairment',
    'Can indicate vestibular dysfunction',
    'May reflect reduced proprioceptive feedback'
  ]
};

// Recommended actions for different metrics
const recommendedActions: Record<string, string[]> = {
  stepLength: [
    'Consider strength training for lower extremities',
    'Evaluate for joint pain or stiffness',
    'Consider gait training exercises'
  ],
  stepSymmetry: [
    'Address potential pain sources',
    'Balance training exercises',
    'Evaluate footwear for proper support'
  ],
  walkingSpeed: [
    'Gradually increase daily walking distance',
    'Consider cardiovascular fitness program',
    'Review medications that may affect energy levels'
  ],
  cadence: [
    'Rhythmic auditory stimulation exercises',
    'Evaluate for increased fatigue',
    'Consider gait training'
  ],
  sitToStandTime: [
    'Leg strengthening exercises',
    'Practice sit-to-stand transitions daily',
    'Evaluate chair height for optimal positioning'
  ],
  balanceScore: [
    'Implement balance training program',
    'Consider tai chi or similar exercises',
    'Evaluate home for fall hazards'
  ],
  dailyStepCount: [
    'Set gradual step count goals',
    'Identify barriers to physical activity',
    'Schedule regular walking times'
  ],
  variabilityIndex: [
    'Dual-task training exercises',
    'Gait stabilization techniques',
    'Consider physical therapy assessment'
  ],
  turnAngle: [
    'Practice turning exercises',
    'Neck and trunk mobility exercises',
    'Evaluate vision and visual scanning'
  ],
  posturalSway: [
    'Standing balance exercises',
    'Consider proprioceptive training',
    'Evaluate for vestibular issues'
  ]
};

// Potential causes for changes in different metrics
const potentialCauses: Record<string, string[]> = {
  stepLength: [
    'Joint pain or stiffness',
    'Reduced muscle strength',
    'Fear of falling',
    'Footwear changes'
  ],
  stepSymmetry: [
    'Pain in one leg or foot',
    'Muscle weakness on one side',
    'Previous injury compensation',
    'Joint stiffness'
  ],
  walkingSpeed: [
    'Cardiovascular changes',
    'Reduced aerobic capacity',
    'Pain during movement',
    'Medication side effects',
    'Fatigue or sleep issues'
  ],
  cadence: [
    'Neurological changes',
    'Balance confidence',
    'Pain or discomfort',
    'Adaptations to environment'
  ],
  sitToStandTime: [
    'Lower extremity weakness',
    'Joint pain (knees, hips)',
    'Balance concerns',
    'Reduced core strength'
  ],
  balanceScore: [
    'Inner ear issues',
    'Vision changes',
    'Reduced proprioception',
    'Medication side effects',
    'Neurological changes'
  ],
  dailyStepCount: [
    'Pain with walking',
    'Reduced endurance',
    'Environmental barriers',
    'Weather changes',
    'Motivational factors'
  ],
  variabilityIndex: [
    'Attention or cognitive changes',
    'Fatigue during walking',
    'Environmental adaptations',
    'Compensatory strategies'
  ],
  turnAngle: [
    'Neck stiffness',
    'Vestibular issues',
    'Fear of falling during turns',
    'Visual field changes'
  ],
  posturalSway: [
    'Vestibular dysfunction',
    'Proprioceptive changes',
    'Medication effects',
    'Neurological changes',
    'Fatigue or weakness'
  ]
};

// Calculate trends from movement data for a specific metric
export const calculateTrend = (
  dataPoints: MovementDataPoint[], 
  metricId: string,
  startDate: Date,
  endDate: Date
): MovementTrend => {
  // Extract values for the specific metric and filter by date range
  const filteredData = dataPoints
    .filter(point => 
      point.timestamp >= startDate && 
      point.timestamp <= endDate &&
      metricId in point.metrics
    )
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  
  // Extract values and timestamps
  const values = filteredData.map(point => ({
    timestamp: point.timestamp,
    value: point.metrics[metricId]
  }));
  
  // Calculate average
  const sum = values.reduce((total, point) => total + point.value, 0);
  const averageValue = values.length > 0 ? sum / values.length : 0;
  
  // Calculate standard deviation
  const squareDiffs = values.map(point => Math.pow(point.value - averageValue, 2));
  const avgSquareDiff = squareDiffs.length > 0 ? 
    squareDiffs.reduce((total, diff) => total + diff, 0) / squareDiffs.length : 0;
  const standardDeviation = Math.sqrt(avgSquareDiff);
  
  // Calculate slope (rate of change) using linear regression
  let slope = 0;
  if (values.length > 1) {
    // Convert dates to x values (days from start)
    const startTime = startDate.getTime();
    const xValues = values.map(point => (point.timestamp.getTime() - startTime) / (24 * 60 * 60 * 1000));
    const yValues = values.map(point => point.value);
    
    // Calculate means
    const meanX = xValues.reduce((sum, x) => sum + x, 0) / xValues.length;
    const meanY = yValues.reduce((sum, y) => sum + y, 0) / yValues.length;
    
    // Calculate slope using least squares method
    const numerator = xValues.reduce((sum, x, i) => sum + (x - meanX) * (yValues[i] - meanY), 0);
    const denominator = xValues.reduce((sum, x) => sum + Math.pow(x - meanX, 2), 0);
    
    slope = denominator !== 0 ? numerator / denominator : 0;
  }
  
  return {
    metricId,
    values,
    startDate,
    endDate,
    averageValue,
    standardDeviation,
    slope
  };
};

// Analyze data to detect subtle changes
export const detectSubtleChanges = (
  dataPoints: MovementDataPoint[],
  metrics: MovementMetric[],
  config: AnalysisConfiguration = defaultAnalysisConfig
): DetectedChange[] => {
  const detectedChanges: DetectedChange[] = [];
  
  if (dataPoints.length < config.minDataPointsRequired) {
    console.warn('Insufficient data points for reliable analysis');
    return [];
  }
  
  // Sort data points by timestamp
  const sortedData = [...dataPoints].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  
  // Get date range for analysis
  const endDate = new Date(Math.max(...sortedData.map(point => point.timestamp.getTime())));
  const startDate = new Date(endDate.getTime() - config.analysisWindowDays * 24 * 60 * 60 * 1000);
  
  // Split the time window in half to compare earlier period vs later period
  const midpointDate = new Date(startDate.getTime() + (endDate.getTime() - startDate.getTime()) / 2);
  
  // Get metrics that are enabled in config
  const enabledMetricIds = metrics
    .filter(metric => config.enabledMetrics.includes(metric.type))
    .map(metric => metric.id);
  
  // For each metric, analyze trends
  enabledMetricIds.forEach(metricId => {
    const metricInfo = metrics.find(m => m.id === metricId);
    if (!metricInfo) return;
    
    // Calculate trends for earlier and later periods
    const earlierTrend = calculateTrend(sortedData, metricId, startDate, midpointDate);
    const laterTrend = calculateTrend(sortedData, metricId, midpointDate, endDate);
    
    if (earlierTrend.values.length === 0 || laterTrend.values.length === 0) {
      return; // Skip if either period has no data
    }
    
    // Calculate percent change between periods
    const percentageChange = ((laterTrend.averageValue - earlierTrend.averageValue) / earlierTrend.averageValue) * 100;
    const absoluteChange = laterTrend.averageValue - earlierTrend.averageValue;
    
    // Determine if change is significant based on thresholds
    const absPercentChange = Math.abs(percentageChange);
    let significance: ChangeSignificance = 'MINOR';
    
    // Adjust thresholds based on sensitivity level
    const sensitivityMultiplier = 
      config.sensitivityLevel === 'HIGH' ? 0.8 : 
      config.sensitivityLevel === 'LOW' ? 1.2 : 1.0;
    
    const thresholds = config.detectionThresholds[metricInfo.type];
    if (absPercentChange >= thresholds.significant * sensitivityMultiplier) {
      significance = 'SIGNIFICANT';
    } else if (absPercentChange >= thresholds.moderate * sensitivityMultiplier) {
      significance = 'MODERATE';
    } else if (absPercentChange >= thresholds.minor * sensitivityMultiplier) {
      significance = 'MINOR';
    } else {
      return; // No significant change detected
    }
    
    // Determine direction of change
    let direction: ChangeDirection;
    if (percentageChange > 0) {
      direction = 'IMPROVING';
      
      // Some metrics improve when they decrease (e.g., sit-to-stand time, variability)
      if (metricId === 'sitToStandTime' || metricId === 'variabilityIndex' || metricId === 'posturalSway') {
        direction = 'DECLINING';
      }
    } else if (percentageChange < 0) {
      direction = 'DECLINING';
      
      // Some metrics decline when they increase
      if (metricId === 'sitToStandTime' || metricId === 'variabilityIndex' || metricId === 'posturalSway') {
        direction = 'IMPROVING';
      }
    } else {
      direction = 'UNKNOWN';
    }
    
    // Create a description
    const changeVerb = direction === 'IMPROVING' ? 'improved' : direction === 'DECLINING' ? 'declined' : 'changed';
    
    const description = `${metricInfo.name} has ${changeVerb} by ${Math.abs(percentageChange).toFixed(1)}% over the past ${config.analysisWindowDays} days, from ${earlierTrend.averageValue.toFixed(2)} to ${laterTrend.averageValue.toFixed(2)} ${metricInfo.unit}.`;
    
    // Get metric-specific information
    const healthImpls = healthImplications[metricId] || [];
    const recActions = recommendedActions[metricId] || [];
    const potCauses = potentialCauses[metricId] || [];
    
    detectedChanges.push({
      id: `change-${metricId}-${Date.now()}`,
      metricId,
      metricName: metricInfo.name,
      metricType: metricInfo.type,
      description,
      significance,
      direction,
      detectedAt: new Date(),
      datePeriod: {
        start: startDate,
        end: endDate
      },
      percentageChange,
      absoluteChange,
      isReviewed: false,
      isAddressedInCareplan: false,
      healthImplications: healthImpls,
      recommendedActions: recActions,
      potentialCauses: potCauses
    });
  });
  
  // Find related changes (changes in the same metric category)
  detectedChanges.forEach(change => {
    const relatedChanges = detectedChanges
      .filter(c => 
        c.id !== change.id && 
        c.metricType === change.metricType &&
        c.direction === change.direction
      )
      .map(c => c.id);
    
    if (relatedChanges.length > 0) {
      change.relatedChanges = relatedChanges;
    }
  });
  
  return detectedChanges;
}; 